import {
  type Context,
  context,
  DiagConsoleLogger,
  DiagLogLevel,
  diag,
  propagation,
  ROOT_CONTEXT,
  type Span,
  SpanKind,
  type SpanOptions,
  SpanStatusCode,
  trace,
} from '@opentelemetry/api';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator } from '@opentelemetry/core';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
  SimpleSpanProcessor,
  type SpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { Stage } from '@org-avp/models';
import { DIST_ENDPOINTS } from '@org-avp/naap-models';
import * as packageJson from '../../package.json';

let tracingInitialized = false;

type WithSpanOptions = Omit<SpanOptions, 'root'> & {
  ctx?: Context;
};

export type TraceContextCarrier = {
  readonly traceparent?: string;
  readonly tracestate?: string;
  readonly baggage?: string;
};

/**
 * Derive the deployment stage from the current hostname.
 *
 * The demo-iframe-app is hosted at:
 *   - naap-demo-iframe-app.develop.avp.tech  → "develop"
 *   - naap-demo-iframe-app.preprod.avp.tech  → "preprod"
 *   - naap-demo-iframe-app.avp.tech          → "prod"
 *   - localhost / other                      → "develop" (fallback)
 */
function deriveStage(): Stage {
  const hostname = globalThis.location?.hostname ?? '';
  if (hostname.includes('.develop.')) return Stage.DEVELOP;
  if (hostname.includes('.preprod.')) return Stage.PREPROD;
  if (hostname.endsWith('.avp.tech') && !hostname.includes('.develop.') && !hostname.includes('.preprod.'))
    return Stage.PROD;

  return Stage.DEVELOP;
}

export function initializeTracing(): void {
  if (tracingInitialized) return;
  tracingInitialized = true;

  const stage = deriveStage();
  diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.WARN);

  const spanProcessors: SpanProcessor[] = [];

  if (stage === Stage.DEVELOP) {
    spanProcessors.push(new SimpleSpanProcessor(new ConsoleSpanExporter()));
  }

  const stageConfig = DIST_ENDPOINTS[stage];
  spanProcessors.push(
    new BatchSpanProcessor(
      new OTLPTraceExporter({
        url: `${stageConfig.baseUrl}/v1/traces`,
        headers: { 'X-API-Key': stageConfig.apiKey },
        concurrencyLimit: 2,
      }),
      {
        maxExportBatchSize: 5,
        scheduledDelayMillis: 15_000,
        maxQueueSize: 50,
        exportTimeoutMillis: 10_000,
      },
    ),
  );

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      'service.name': 'naap-demo-iframe-app',
      'service.namespace': 'naap',
      'service.version': packageJson.version,
      'deployment.environment.name': stage,
    }),
    spanProcessors,
  });

  provider.register({
    // Match kiosk `withSpan` semantics by installing a browser context manager so
    // active-parent spans survive async boundaries in event/callback-driven code.
    contextManager: new ZoneContextManager(),
    propagator: new CompositePropagator({
      propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()],
    }),
  });
}

export function getOtelTracer() {
  return trace.getTracer('naap-demo-iframe-app', packageJson.version);
}

/**
 * Ordinary active-parent span helper.
 *
 * Mirrors the kiosk helper semantics: it honors the provided/active parent
 * context and intentionally does not force `root`.
 */
export async function withSpan<T>(
  name: string,
  fn: (span: Span) => Promise<T> | T,
  opts?: WithSpanOptions,
): Promise<T> {
  const tracer = getOtelTracer();
  const { ctx, ...spanOptions } = opts ?? {};
  const parentCtx = ctx ?? context.active();
  const span = tracer.startSpan(name, spanOptions, parentCtx);

  return context.with(trace.setSpan(parentCtx, span), async () => {
    try {
      return await fn(span);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error(`[${name}] span failed:`, err);
      span.setStatus({ code: SpanStatusCode.ERROR, message: err.message });
      throw error;
    } finally {
      span.end();
    }
  });
}

/**
 * Extracts an OTel Context from trace context headers.
 * Returns ROOT_CONTEXT if no trace context headers are provided.
 */
export function extractTraceContext(traceContext: string | TraceContextCarrier | undefined): Context {
  if (!traceContext) return ROOT_CONTEXT;

  // TODO Remove this once the deprecated traceparent field has been removed from the kiosk app sdk
  const normalized = typeof traceContext === 'string' ? { traceparent: traceContext } : traceContext;

  const { traceparent, tracestate, baggage: baggageHeader } = normalized;
  if (!traceparent && !tracestate && !baggageHeader) return ROOT_CONTEXT;

  return propagation.extract(ROOT_CONTEXT, {
    traceparent,
    tracestate,
    baggage: baggageHeader,
  });
}

/**
 * Cross-document consumer span helper.
 *
 * Unlike `withSpan`, this intentionally starts a fresh root span and links it to
 * the kiosk-provided context. The kiosk span may already have ended before the
 * iframe handles the message, so making the iframe span a child would violate
 * span containment. We intentionally do not add kiosk-style browser auto-
 * instrumentation here; this helper only fixes explicit cross-iframe consumer work.
 */
export async function withLinkedRootSpan<T>(
  name: string,
  traceContext: string | TraceContextCarrier | undefined,
  fn: (span: Span) => T | Promise<T>,
): Promise<T> {
  const kioskContext = extractTraceContext(traceContext);
  const spanContext = trace.getSpanContext(kioskContext);
  const baggage = propagation.getBaggage(kioskContext);
  const rootContext = baggage ? propagation.setBaggage(ROOT_CONTEXT, baggage) : ROOT_CONTEXT;

  return withSpan(name, fn, {
    kind: SpanKind.INTERNAL,
    links: spanContext ? [{ context: spanContext }] : [],
    ctx: rootContext,
  });
}
